import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { MfaPayload } from '@accountFederation/modules/securityCheck/types';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { getFaceIdLogger } from '@accountFederation/modules/securityCheck/logging';

type MfaStatusCheckParams = {
    mfaPayload: MfaPayload;
    verifiedAction: () => void;
    failedAction: () => void;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useMfaStatusCheck(timeoutInSeconds = 300) {
    let mfaStatusCheckInterval: ReturnType<typeof setInterval>;
    let mfaStatusCheckTimeout: ReturnType<typeof setTimeout>;
    let hasFinished = false;

    const mfaCheckStatusIntervalMs = getAccountConfig().mfaCheckStatusInterval;

    const legacyCheckMfaStatus = ({ mfaPayload, verifiedAction, failedAction }: MfaStatusCheckParams) => {
        hasFinished = false;
        const logMfaEvent = getFaceIdLogger(mfaPayload?.flowName);

        logMfaEvent('mfa_status_check_start');

        stopPolling();
        mfaStatusCheckTimeout = setTimeout(() => {
            stopPolling();
            failedAction();
        }, timeoutInSeconds * 1000);

        try {
            mfaStatusCheckInterval = setInterval(async () => {
                if (
                    hasFinished
                    && getAccountConfig().mfaDuplicateCallMitigationConfig.isMfaStatusCheckMitigationEnabled
                ) {
                    if (getAccountConfig().mfaDuplicateCallMitigationConfig.isLoggingEnabled) {
                        logMfaEvent('mfa_status_check_duplicate_start');
                    }
                    return;
                }
                const response = await authenticationService.getMfaStatus(mfaPayload);

                const statusActions = {
                    verified: verifiedAction,
                    active: verifiedAction,
                    failed: failedAction,
                    expired: failedAction,
                    no_process: failedAction,
                };

                const action = statusActions[response.data.status as keyof typeof statusActions];
                if (action) {
                    // eslint-disable-next-line require-atomic-updates
                    hasFinished = true;

                    logMfaEvent(`mfa_status_${response.data.status}`);

                    stopPolling();
                    action();
                }
            }, mfaCheckStatusIntervalMs);
        } catch (error) {
            logMfaEvent('mfa_status_check_error');
            stopPolling();
            failedAction();
        }
    };

    let lastOtpCheckRequest = '';
    const newCheckMfaStatus = ({ mfaPayload, verifiedAction, failedAction }: MfaStatusCheckParams) => {
        const logMfaEvent = getFaceIdLogger(mfaPayload?.flowName);
        const newOtp = mfaPayload?.otpId || mfaPayload?.otp_id; // TODO: Look into refactor across system to only one field and style

        logMfaEvent('mfa_status_check_start');

        if (newOtp === lastOtpCheckRequest) {
            if (getAccountConfig().mfaDuplicateCallMitigationConfig.isMfaStatusCheckMitigationEnabled) {
                return;
            }

            if (getAccountConfig().mfaDuplicateCallMitigationConfig.isLoggingEnabled) {
                logMfaEvent('mfa_status_check_duplicate_start');
            }
        }

        lastOtpCheckRequest = newOtp;

        const statusActions = {
            verified: verifiedAction, // TODO: Verified should become early exit / duplicate call
            active: verifiedAction,
            failed: failedAction,
            expired: failedAction,
            no_process: failedAction,
        };

        const maxChecks = Math.ceil(timeoutInSeconds / (mfaCheckStatusIntervalMs / 1000));
        let currentChecks = 0;
        const checkStatus = async () => {
            try {
                const response = await authenticationService.getMfaStatus(mfaPayload);

                const action = statusActions[response.data.status as keyof typeof statusActions];
                if (action) {
                    action();
                    logMfaEvent(`mfa_status_${response.data.status}`);
                } else if (currentChecks < maxChecks) {
                    if (getAccountConfig().mfaDuplicateCallMitigationConfig.isLoggingEnabled) {
                        logMfaEvent('mfa_status_check_tick');
                    }

                    currentChecks += 1;
                    setTimeout(checkStatus, 3000);
                } else {
                    logMfaEvent('mfa_status_check_end');
                    failedAction();
                }
            } catch (error) {
                logMfaEvent('mfa_status_check_error');
                failedAction();
            }
        };

        setTimeout(checkStatus, mfaCheckStatusIntervalMs);
    };

    const stopPolling = () => {
        clearInterval(mfaStatusCheckInterval);
        clearTimeout(mfaStatusCheckTimeout);
    };

    return {
        checkMfaStatus: getAccountConfig().mfaDuplicateCallMitigationConfig.isV2CheckerEnabled ? newCheckMfaStatus : legacyCheckMfaStatus,
    };
}
